import { useCallback, useEffect, useRef, useState } from "react";
import {
  Loader2, Search, HelpCircle, StopCircle, AlertCircle,
  Trash2, Pin, PinOff, ArrowLeft, Globe, Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { streamEvents, get, del, api } from "@/lib/api";

type Mode = "grounded" | "prompts";
type Ending = "refusal" | "incomplete" | "truncated";

interface AIResearchProps {
  documentId: number;
}

/**
 * Which audience description the question mode will actually use — the
 * account profile, or this document's own Audience field overriding it.
 * `origin: null` means none is set, or this document type has no audience.
 */
interface AudienceHint {
  origin: "document" | "profile" | null;
  summary: string;
}

export interface ResearchEntry {
  id: number;
  mode: Mode;
  focus: string | null;
  content: string;
  searched: boolean;
  ending: Ending | null;
  pinned: boolean;
  created_at: string;
}

function whenLabel(iso: string) {
  const then = new Date(iso);
  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return then.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const ENDING_NOTE: Record<Ending, string> = {
  refusal: "The model stopped partway through this one.",
  incomplete: "Stopped after the search limit — this is a partial answer.",
  truncated: "Hit the length limit, so this is cut short.",
};

/**
 * Renders model output as plain structured text.
 *
 * Deliberately not markdown-to-HTML: the output is prose we don't control, and
 * piping it through an unsanitised renderer to gain some heading styling is a
 * bad trade. Headings and bullets are enough structure to read by.
 */
function ResearchText({ text }: { text: string }) {
  const lines = text.split("\n");
  return (
    <div className="space-y-1.5">
      {lines.map((line, i) => {
        const heading = /^(#{1,6})\s+(.*)$/.exec(line);
        if (heading) {
          return (
            <h4 key={i} className="text-sm font-semibold mt-3 first:mt-0">
              {heading[2]}
            </h4>
          );
        }
        const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
        if (bullet) {
          return (
            <p key={i} className="text-sm leading-relaxed pl-4 -indent-2">
              — {bullet[1]}
            </p>
          );
        }
        if (!line.trim()) return <div key={i} className="h-1.5" />;
        return (
          <p key={i} className="text-sm leading-relaxed">
            {line}
          </p>
        );
      })}
    </div>
  );
}

/**
 * Research pane, display only.
 *
 * There is no insert button and no copy button, by design. Anything useful
 * here gets retyped in the author's own words — that friction is the point,
 * not an omission. See the boundary in server/ai-research.ts.
 */
export function AIResearch({ documentId }: AIResearchProps) {
  const [mode, setMode] = useState<Mode>("grounded");
  const [focus, setFocus] = useState("");
  const [output, setOutput] = useState("");
  const [ending, setEnding] = useState<Ending | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [searching, setSearching] = useState(false);
  const [history, setHistory] = useState<ResearchEntry[]>([]);
  /** A saved run being re-read, or null when showing the live/last output. */
  const [viewing, setViewing] = useState<ResearchEntry | null>(null);
  const [audience, setAudience] = useState<AudienceHint | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Abort in flight work if the pane unmounts — otherwise we keep paying for
  // tokens nobody will read.
  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  useEffect(() => {
    let cancelled = false;
    get<ResearchEntry[]>(`/ai/documents/${documentId}/research`)
      .then((rows) => {
        if (!cancelled) setHistory(rows || []);
      })
      .catch(() => undefined);
    // Context the author can't see is context they can't correct, so show what
    // the questions are being asked with in mind. Failure is silent: this is a
    // caption, and losing it should never look like the pane is broken.
    get<AudienceHint>(`/ai/documents/${documentId}/audience`)
      .then((hint) => {
        if (!cancelled) setAudience(hint);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [documentId]);

  const run = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setRunning(true);
    setSearching(false);
    setViewing(null);
    setOutput("");
    setEnding(null);
    setError(null);

    try {
      for await (const event of streamEvents(`/ai/documents/${documentId}/research`, {
        body: { mode, focus: focus.trim() || undefined },
        signal: controller.signal,
      })) {
        if (event.data === "[DONE]") break;

        let payload: {
          type?: string;
          status?: string;
          delta?: { type?: string; text?: string };
          ending?: Ending;
          entry?: ResearchEntry;
          error?: { message?: string };
        };
        try {
          payload = JSON.parse(event.data);
        } catch {
          continue;
        }

        if (payload.type === "error") {
          setError(payload.error?.message || "Something went wrong.");
          break;
        }
        if (payload.type === "status") {
          setSearching(payload.status === "searching");
          continue;
        }
        if (payload.type === "ending" && payload.ending) {
          setEnding(payload.ending);
          continue;
        }
        if (payload.type === "saved" && payload.entry) {
          // The server persists on completion and hands the row back, so the
          // history list updates without a refetch.
          const entry = payload.entry;
          setHistory((prev) => [entry, ...prev]);
          continue;
        }
        if (payload.type === "content_block_delta" && payload.delta?.text) {
          const chunk = payload.delta.text;
          // First prose means searching is over.
          setSearching(false);
          setOutput((prev) => prev + chunk);
        }
        if (payload.type === "message_stop") break;
      }
    } catch (err) {
      if ((err as Error)?.name === "AbortError") return;
      const status = (err as { status?: number })?.status;
      setError(
        status === 503
          ? "AI isn't configured on the server."
          : status === 429
            ? "That's a lot of requests — give it a minute."
            : err instanceof Error
              ? err.message
              : "Something went wrong.",
      );
    } finally {
      setRunning(false);
      setSearching(false);
    }
  }, [documentId, mode, focus]);

  const togglePin = async (entry: ResearchEntry) => {
    const next = !entry.pinned;
    // Optimistic: re-sort immediately so the click feels instant.
    setHistory((prev) =>
      [...prev.map((e) => (e.id === entry.id ? { ...e, pinned: next } : e))].sort(
        (a, b) =>
          Number(b.pinned) - Number(a.pinned) ||
          +new Date(b.created_at) - +new Date(a.created_at),
      ),
    );
    try {
      await api(`/ai/research/${entry.id}`, { method: "PATCH", body: { pinned: next } });
    } catch {
      setHistory((prev) =>
        prev.map((e) => (e.id === entry.id ? { ...e, pinned: entry.pinned } : e)),
      );
    }
  };

  const remove = async (entry: ResearchEntry) => {
    setHistory((prev) => prev.filter((e) => e.id !== entry.id));
    if (viewing?.id === entry.id) setViewing(null);
    try {
      await del(`/ai/research/${entry.id}`);
    } catch {
      setHistory((prev) =>
        [...prev, entry].sort(
          (a, b) =>
            Number(b.pinned) - Number(a.pinned) ||
            +new Date(b.created_at) - +new Date(a.created_at),
        ),
      );
    }
  };

  const stop = () => {
    abortRef.current?.abort();
    setRunning(false);
    setSearching(false);
  };

  // Follow the output as it streams, but only while the user is already at the
  // bottom — yanking the pane while they're reading earlier text is worse than
  // not following at all.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [output]);

  return (
    <div className="flex flex-col gap-3">
      <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)}>
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="grounded">
            <Search className="h-3.5 w-3.5 mr-1.5" />
            Background
          </TabsTrigger>
          <TabsTrigger value="prompts">
            <HelpCircle className="h-3.5 w-3.5 mr-1.5" />
            Questions
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <p className="text-xs text-muted-foreground">
        {mode === "grounded"
          ? "Context, cross-references, and the honest difficulties in the material. Claims are searched and cited where possible — check anything before you rely on it."
          : "Questions to sharpen your own thinking. It won't answer them for you, and it won't write your illustrations."}
      </p>

      {mode === "prompts" && audience?.origin && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Users className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span className="line-clamp-2">
            Asked with{" "}
            {audience.origin === "document"
              ? "this document's audience"
              : "your congregation"}{" "}
            in mind: <span className="italic">{audience.summary}</span>
          </span>
        </p>
      )}

      <div className="flex gap-2">
        <Input
          placeholder={
            mode === "grounded" ? "Narrow it down (optional)" : "Focus on… (optional)"
          }
          value={focus}
          onChange={(e) => setFocus(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !running) {
              e.preventDefault();
              void run();
            }
          }}
        />
        {running ? (
          <Button type="button" variant="outline" size="icon" onClick={stop} title="Stop">
            <StopCircle className="h-4 w-4" />
          </Button>
        ) : (
          <Button type="button" onClick={() => void run()}>
            {output ? "Again" : "Go"}
          </Button>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2.5">
          <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <div ref={scrollRef} className="max-h-[60vh] overflow-y-auto">
        {viewing ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2 pb-2 border-b">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setViewing(null)}
              >
                <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                Back
              </Button>
              <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                {viewing.mode === "grounded" ? "Background" : "Questions"}
                {viewing.searched && (
                  <Globe className="h-3 w-3" aria-label="Sources were searched" />
                )}
                · {whenLabel(viewing.created_at)}
              </span>
            </div>
            {viewing.focus && (
              <p className="text-xs text-muted-foreground italic">"{viewing.focus}"</p>
            )}
            <ResearchText text={viewing.content} />
            {viewing.ending && (
              <p className="text-xs text-muted-foreground mt-3 border-t pt-2">
                {ENDING_NOTE[viewing.ending]}
              </p>
            )}
            {!viewing.searched && viewing.mode === "grounded" && (
              <p className="text-xs text-muted-foreground border-t pt-2">
                No sources were searched on this run — treat the claims as
                unverified.
              </p>
            )}
          </div>
        ) : output ? (
          <>
            <ResearchText text={output} />
            {running && (
              <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {/* Searching resumes between passages of prose, so this has to
                    stay visible after the first text, not just before it. */}
                {searching && <span>checking sources…</span>}
              </div>
            )}
            {ending && (
              <p className="text-xs text-muted-foreground mt-3 border-t pt-2">
                {ENDING_NOTE[ending]}
              </p>
            )}
          </>
        ) : running ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            {searching
              ? "Checking sources — this can take a minute…"
              : mode === "grounded"
                ? "Reading the draft…"
                : "Thinking…"}
          </div>
        ) : (
          !error && (
            <p className="text-sm text-muted-foreground text-center py-8">
              Nothing yet — hit Go.
            </p>
          )
        )}
      </div>

      {output && !running && (
        <p className="text-xs text-muted-foreground border-t pt-2">
          Nothing here goes into your document on its own. Take what's useful and
          write it in your own words.
        </p>
      )}

      {history.length > 0 && (
        <div className="border-t pt-3 space-y-1">
          <p className="text-xs font-medium text-muted-foreground mb-1.5">
            Earlier on this sermon
          </p>
          {history.map((entry) => (
            <div
              key={entry.id}
              className="group flex items-start gap-1.5 rounded-md hover:bg-muted/60 px-1.5 py-1"
            >
              <button
                type="button"
                className="flex-1 text-left min-w-0"
                onClick={() => setViewing(entry)}
              >
                <span className="flex items-center gap-1.5 text-xs">
                  {entry.pinned && <Pin className="h-3 w-3 shrink-0" />}
                  <span className="font-medium">
                    {entry.mode === "grounded" ? "Background" : "Questions"}
                  </span>
                  {entry.searched && <Globe className="h-3 w-3 text-muted-foreground shrink-0" />}
                  <span className="text-muted-foreground shrink-0">
                    {whenLabel(entry.created_at)}
                  </span>
                </span>
                <span className="block text-xs text-muted-foreground truncate mt-0.5">
                  {entry.focus || entry.content.replace(/[#*]/g, "").slice(0, 70)}
                </span>
              </button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 opacity-0 group-hover:opacity-100 shrink-0"
                title={entry.pinned ? "Unpin" : "Pin"}
                onClick={() => void togglePin(entry)}
              >
                {entry.pinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 opacity-0 group-hover:opacity-100 shrink-0"
                title="Delete"
                onClick={() => void remove(entry)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
